/**
 * Assemble the effective OpenSearch query for a report from its configured
 * state — company scoping, build filters (layout-aware), user filters, and the
 * group-by HAVING split — in one place.
 *
 * Three surfaces must agree on these semantics or they silently drift apart:
 * the interactive preview (preview-table), the searchBody persisted at report
 * save (report-builder), and the manual export from the viewer (report-view).
 * `buildReportQueryCore` produces the shared query base; `buildReportSearchBody`
 * finishes it into a complete body (sort, _source projection, aggregations)
 * for persistence or export.
 */

import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { camelToSnakeCase } from 'tio-common/utils/string-case-conversion';
import { buildConfiguredFilterClauses } from './filter-query-builder';
import { buildHavingSelectors } from './aggregation-having';
import { buildAggregationQuery } from './aggregation-query-builder';
import { parseFilterKey, type FilterLayout } from './filter-operators';
import { resolveRelativeDateFilters } from './relative-date-resolver';
import { sortsToStrings, type SortEntry } from './sort-helpers';
import { isSqlDataset } from './dataset-utils';
import type { ColumnAggregationMap, DateGroupingMap } from './aggregation-types';
import type { FieldOption } from './opensearch-field-mapper';
import type { FilterClauseContext } from './filter-clause-builder';

/**
 * Resolve filter keys to OpenSearch paths + field metadata for clause
 * dispatch. Keys missing from `fields` (e.g. a saved filter on a field later
 * removed from the dataset) fall back to snake_casing the camelCase key —
 * the index is snake_case, so this still targets the real path for flat fields.
 */
function filterClauseContextFor(fields: FieldOption[]): FilterClauseContext {
  const fieldByKey: Record<string, FieldOption> = {};
  for (const f of fields) {
    fieldByKey[f.key] = f;
  }
  return { pathFor: (k: string) => fieldByKey[k]?.path || camelToSnakeCase(k), fieldByKey };
}

/**
 * Reports saved before filter layouts existed carry build filter values with
 * no layout — treat every value as its own top-level AND slot rather than
 * dropping it. (Dashboard widgets keep strict layout-only semantics; this
 * fallback is deliberately scoped to the report query paths.)
 */
function effectiveBuildFilterLayout(
  layout: FilterLayout,
  buildFilterValues: Record<string, unknown>
): FilterLayout {
  if (layout.length > 0) return layout;
  const baseKeys = [
    ...new Set(Object.keys(buildFilterValues).map((k) => parseFilterKey(k).baseKey)),
  ];
  return baseKeys.map((fieldKey) => ({ type: 'filter' as const, fieldKey }));
}

export interface ReportQueryCoreOptions {
  modelType: string;
  /** All dataset fields — used for path resolution and queryHints dispatch. */
  fields: FieldOption[];
  buildFilterValues?: Record<string, unknown>;
  buildFilterLayout?: FilterLayout;
  userFilterValues?: Record<string, unknown>;
  /** Single-company scope (body term — skipped for SQL datasets, which scope by request param). */
  companyId?: string;
  /** Multi-company scope; wins over companyId when non-empty. */
  companyIds?: string[];
  companyFilterField?: string;
  companyFilterOperator?: 'in' | 'not_in';
  /**
   * Embed the companyId term even for SQL datasets. Interactive requests must
   * NOT (param-kind catalogs 400 on a body term — scoping rides the request
   * param), but persisted/export bodies are validated server-side by clause
   * inspection (SearchPolicy), so they keep the term like they always have.
   */
  forceSqlCompanyTerm?: boolean;
  /** Group-by mode inputs — divert metric-column filters to HAVING selectors. */
  isAggregated?: boolean;
  groupByFieldKeys?: string[];
  /** Selected report columns (metric detection for HAVING + agg construction). */
  columns?: FieldOption[];
}

export interface ReportQueryCore {
  /** Builder with company scoping + all WHERE filter clauses applied. */
  builder: OpenSearchQueryBuilder;
  /** HAVING (bucket_selector) output for group-by mode; empty otherwise. */
  having: { selectors: Record<string, unknown>; consumedFields: Set<string> };
  isAggregated: boolean;
}

export function buildReportQueryCore(opts: ReportQueryCoreOptions): ReportQueryCore {
  const builder = new OpenSearchQueryBuilder();
  const clauseCtx = filterClauseContextFor(opts.fields);

  // Company scoping — field name varies per dataset. SQL datasets take the
  // SINGLE company as a request param (never a DSL filter — param-kind
  // catalogs may not even project the tenant column, and the backend
  // force-scopes anyway); a multi-company set stays a body filter, which
  // only column-kind datasets reach and those always declare the column.
  const companyIds = opts.companyIds ?? [];
  const scopesByCompany =
    companyIds.length > 0 ||
    (opts.companyId && (opts.forceSqlCompanyTerm || !isSqlDataset(opts.modelType)));
  if (scopesByCompany && !opts.companyFilterField) {
    console.warn('[ReportQueryCore] companyFilterField not provided; defaulting to "company_id"');
  }
  const companyField = opts.companyFilterField || 'company_id';
  if (companyIds.length > 0) {
    if (opts.companyFilterOperator === 'not_in') {
      builder.mustNotTerms(companyField, companyIds);
    } else {
      builder.mustTerms(companyField, companyIds);
    }
  } else if (opts.companyId && scopesByCompany) {
    builder.mustTerm(companyField, opts.companyId);
  }

  const buildFilterValues = opts.buildFilterValues ?? {};
  const buildFilterLayout = effectiveBuildFilterLayout(
    opts.buildFilterLayout ?? [],
    buildFilterValues
  );
  const userFilterValues = opts.userFilterValues ?? {};

  // In group-by mode, filters on aggregated metric columns become HAVING
  // (bucket_selector) clauses rather than pre-aggregation document filters.
  // Only top-level AND filters are eligible — OR-group members stay as WHERE
  // so their OR semantics are preserved.
  const isAggregated = Boolean(opts.isAggregated) && (opts.groupByFieldKeys ?? []).length > 0;
  let having: ReportQueryCore['having'] = { selectors: {}, consumedFields: new Set() };
  if (isAggregated) {
    const resolvedUser = resolveRelativeDateFilters(userFilterValues);
    const resolvedBuild = resolveRelativeDateFilters(buildFilterValues);
    const andEligible: Record<string, unknown> = { ...resolvedUser };
    const topLevel = new Set(
      buildFilterLayout
        .filter((s) => s.type === 'filter')
        .map((s) => (s.type === 'filter' ? s.fieldKey : ''))
    );
    for (const [key, value] of Object.entries(resolvedBuild)) {
      if (topLevel.has(parseFilterKey(key).baseKey)) andEligible[key] = value;
    }
    having = buildHavingSelectors(andEligible, {
      columns: opts.columns ?? [],
      groupByKeys: opts.groupByFieldKeys ?? [],
    });
  }
  const isConsumed = (key: string) => having.consumedFields.has(parseFilterKey(key).baseKey);

  const clauses = buildConfiguredFilterClauses(
    { buildFilterValues, buildFilterLayout, userFilterValues },
    clauseCtx,
    { skipKey: isConsumed }
  );
  for (const clause of clauses) {
    builder.addFilter(clause);
  }

  return { builder, having, isAggregated };
}

export interface ReportSearchBodyOptions extends ReportQueryCoreOptions {
  columns: FieldOption[];
  sorts?: SortEntry[];
  columnAggregationMap?: ColumnAggregationMap;
  dateGroupingIntervals?: DateGroupingMap;
}

/**
 * Complete query body for the report's current state — what the server-side
 * export (or the persisted searchBody) should run. Group-by reports get the
 * nested aggregation shape (`size: 0` + `aggs`), which is the form the export
 * pipeline's AggregationFlattener consumes. Company scoping stays embedded
 * even for SQL datasets: the export/persistence consumers validate scope by
 * inspecting body clauses. (Any default `size`/`from` is ignored server-side —
 * the standard export scrolls the full result set.)
 */
export function buildReportSearchBody(opts: ReportSearchBodyOptions): Record<string, unknown> {
  const { builder, having, isAggregated } = buildReportQueryCore({
    ...opts,
    forceSqlCompanyTerm: true,
  });
  const sorts = opts.sorts ?? [];

  if (sorts.length > 0) {
    builder.setSort(sortsToStrings(sorts));
  }

  // Source fields — ordered column paths preserve column arrangement in the CSV
  const selectedSourcePaths = opts.columns
    .map((col) => col.path || col.key)
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
  builder.selectSourceFields(selectedSourcePaths);

  if (isAggregated) {
    const aggQuery = buildAggregationQuery(
      opts.groupByFieldKeys ?? [],
      opts.columns,
      opts.columnAggregationMap ?? {},
      sorts,
      opts.dateGroupingIntervals,
      having.selectors
    );
    return { ...builder.build(), size: 0, aggs: aggQuery };
  }

  return builder.build();
}
